import React, { useState, useEffect } from "react";
import { useAuth } from './AuthContext';
import { useNavigate } from 'react-router-dom';
import './login.css';

const Login = () => {
    const navigate = useNavigate();
    const { user, loading, login, loginWithGoogle } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSafariWarning, setShowSafariWarning] = useState(false);
    const [isSafari] = useState(() => 
        /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent)
    );

    useEffect(() => {
        if (user) {
            navigate('/');
        }
    }, [user, navigate]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        setIsSubmitting(true);

        // Basic validation
        if (!email || !password) {
            setError('Please fill in all fields');
            setIsSubmitting(false);
            return;
        }

        try {
            await login(email, password);
        } catch (error) {
            console.error('Login failed:', error);
            // Handle different error cases
            if (error.code === 401) {
                setError('Invalid email or password');
            } else if (error.code === 429) {
                setError('Too many attempts. Please try again later');
            } else {
                setError('An error occurred. Please try again');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleGoogleLogin = async () => {
        try {
            await loginWithGoogle();
        } catch (error) {
            console.error('Google login failed:', error);
            setError('Authentication failed. Please try again.');
        }
    };

    return (
        <div className="login-container">
            <div className="logo-section">
                <div className="logo-content">
                    <img src="./logoNav.png" alt="Company Logo" className="company-logo" />
                    <h1 className="company-name">Parfect Performance</h1>
                    <p className="company-slogan">Elevate Your Golf Game</p>
                </div>
            </div>
            
            <div className="login-section">
                <div className="login-box">
                    <h2>Welcome Back {user ? `, ${user.name}`: ""}</h2>
                    <p className="login-subtitle">Please enter your details</p>
                    
                    {error && (
                        <div className="error-message">
                            <i className="fas fa-exclamation-circle"></i>
                            {error}
                        </div>
                    )}
                    
                    <form className="login-form" onSubmit={handleLogin}>
                        <div className="form-group">
                            <label htmlFor="email">Email</label>
                            <input 
                                type="email" 
                                id="email" 
                                placeholder="Enter your email"
                                className={`form-input ${error ? 'input-error' : ''}`}
                                value={email}
                                onChange={e => {
                                    setEmail(e.target.value);
                                    setError(''); // Clear error when user types
                                }}
                            />
                        </div>
                        
                        <div className="form-group">
                            <label htmlFor="password">Password</label>
                            <input 
                                type="password" 
                                id="password" 
                                placeholder="Enter your password"
                                className={`form-input ${error ? 'input-error' : ''}`}
                                value={password}
                                onChange={e => {
                                    setPassword(e.target.value);
                                    setError(''); // Clear error when user types
                                }}
                            />
                        </div>

                        <button 
                            type="submit" 
                            className="login-button"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'Signing In...' : 'Sign In'}
                        </button>

                        <div className="divider">
                            <span>or continue with</span>
                        </div>

                        <div className="social-buttons">
                            <button 
                                type="button"
                                className="social-button google"
                                onClick={handleGoogleLogin}
                                onMouseEnter={() => isSafari && setShowSafariWarning(true)}
                                onMouseLeave={() => setShowSafariWarning(false)}
                            >
                                <i className="fab fa-google"></i>
                            </button>
                            <button className="social-button apple">
                                <i className="fab fa-apple"></i>
                            </button>
                            <button className="social-button microsoft">
                                <i className="fab fa-microsoft"></i>
                            </button>
                        </div>

                        <p className="signup-prompt">
                            Don't have an account? 
                            <a href="/signup" className="signup-link">Sign up</a>
                        </p>
                    </form>
                </div>
            </div>
            
            {showSafariWarning && isSafari && (
                <div className="safari-warning">
                    <div className="warning-content">
                        <p>⚠️ Safari requires third-party cookies to be enabled for Google login.</p>
                        <p className="warning-instructions">
                            To enable: Safari Preferences → Privacy → Uncheck 'Prevent cross-site tracking'
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Login;
  